import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useGetImeiByNumberQuery,
  useSearchImeisQuery,
  useDirectSupplierExchangeMutation,
  useReassignSupplierMutation,
  useCorrectImeiMutation,
} from '../../../store/api/imeiApi';
import { useGetSuppliersQuery } from '../../../store/api/purchaseApi';

function ImeiTracking() {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [queryTerm, setQueryTerm] = useState('');

  // Table directory filters
  const [dirSearch, setDirSearch] = useState('');
  const [dirStatus, setDirStatus] = useState('');
  const [dirPage, setDirPage] = useState(1);

  // Modals state
  const [exchangeModalOpen, setExchangeModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [correctModalOpen, setCorrectModalOpen] = useState(false);

  // Form states
  const [exchangeForm, setExchangeForm] = useState({
    newImei: '',
    newSecondaryImei: '',
    supplierId: '',
    supplierRmaRef: '',
    reason: '',
  });
  const [reassignForm, setReassignForm] = useState({
    supplierId: '',
    reason: '',
  });
  const [correctForm, setCorrectForm] = useState({
    imei: '',
    secondaryImei: '',
    reason: '',
  });

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Queries & Mutations
  const { data: historyData, isLoading, isError, refetch } = useGetImeiByNumberQuery(queryTerm, {
    skip: !queryTerm,
  });

  const { data: directoryData, isLoading: dirLoading } = useSearchImeisQuery(
    { search: dirSearch, status: dirStatus, page: dirPage, limit: 20 },
    { skip: activeTab !== 1 }
  );

  const { data: suppliersData } = useGetSuppliersQuery();
  const suppliers = suppliersData?.suppliers || suppliersData || [];

  const [directSupplierExchange, { isLoading: isExchanging }] = useDirectSupplierExchangeMutation();
  const [reassignSupplier, { isLoading: isReassigning }] = useReassignSupplierMutation();
  const [correctImei, { isLoading: isCorrecting }] = useCorrectImeiMutation();

  const handleSearch = (e) => {
    e?.preventDefault();
    const newTerm = searchTerm.trim();
    if (newTerm === queryTerm && newTerm !== '') {
      refetch();
    } else {
      setQueryTerm(newTerm);
    }
  };

  const handleInspectImei = (imeiNumber) => {
    setSearchTerm(imeiNumber);
    setQueryTerm(imeiNumber);
    setActiveTab(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'success';
      case 'sold': return 'info';
      case 'returned': return 'warning';
      case 'supplier_replaced': return 'secondary';
      case 'rma': return 'error';
      default: return 'default';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'received':
      case 'purchase': return 'success';
      case 'sold':
      case 'sale': return 'info';
      case 'returned':
      case 'return': return 'warning';
      case 'rma_received': return 'secondary';
      case 'rma_sent': return 'warning';
      case 'correction': return 'primary';
      default: return 'default';
    }
  };

  // Open Handlers
  const handleOpenExchange = () => {
    if (!historyData?.imeiRecord) return;
    const currentSupplierId =
      historyData.imeiRecord.supplierId ||
      historyData.imeiRecord.purchaseOrderItem?.purchaseOrder?.supplier?.id ||
      '';
    setExchangeForm({
      newImei: '',
      newSecondaryImei: '',
      supplierId: currentSupplierId,
      supplierRmaRef: '',
      reason: '',
    });
    setExchangeModalOpen(true);
  };

  const handleOpenReassign = () => {
    if (!historyData?.imeiRecord) return;
    const currentSupplierId =
      historyData.imeiRecord.supplierId ||
      historyData.imeiRecord.purchaseOrderItem?.purchaseOrder?.supplier?.id ||
      '';
    setReassignForm({
      supplierId: currentSupplierId,
      reason: '',
    });
    setReassignModalOpen(true);
  };

  const handleOpenCorrect = () => {
    if (!historyData?.imeiRecord) return;
    setCorrectForm({
      imei: historyData.imeiRecord.imei,
      secondaryImei: historyData.imeiRecord.secondaryImei || '',
      reason: '',
    });
    setCorrectModalOpen(true);
  };

  // Submit Handlers
  const handleExchangeSubmit = async (e) => {
    e.preventDefault();
    if (!exchangeForm.newImei.trim()) {
      alert('Please enter the new replacement IMEI.');
      return;
    }
    try {
      const res = await directSupplierExchange({
        id: historyData.imeiRecord.id,
        newImei: exchangeForm.newImei.trim(),
        newSecondaryImei: exchangeForm.newSecondaryImei.trim() || undefined,
        supplierId: exchangeForm.supplierId || undefined,
        supplierRmaRef: exchangeForm.supplierRmaRef.trim() || undefined,
        reason: exchangeForm.reason.trim() || undefined,
      }).unwrap();

      setNotification({
        open: true,
        message: res.message || 'Device replaced with supplier successfully!',
        severity: 'success',
      });
      setExchangeModalOpen(false);
      // Auto inspect the new IMEI
      setSearchTerm(exchangeForm.newImei.trim());
      setQueryTerm(exchangeForm.newImei.trim());
    } catch (err) {
      alert(err.data?.message || 'Failed to exchange device with supplier.');
    }
  };

  const handleReassignSubmit = async (e) => {
    e.preventDefault();
    if (!reassignForm.supplierId) {
      alert('Please select a supplier.');
      return;
    }
    try {
      const res = await reassignSupplier({
        id: historyData.imeiRecord.id,
        supplierId: reassignForm.supplierId,
        reason: reassignForm.reason.trim() || undefined,
      }).unwrap();

      setNotification({
        open: true,
        message: res.message || 'Supplier successfully reassigned.',
        severity: 'success',
      });
      setReassignModalOpen(false);
      refetch();
    } catch (err) {
      alert(err.data?.message || 'Failed to reassign supplier.');
    }
  };

  const handleCorrectSubmit = async (e) => {
    e.preventDefault();
    if (!correctForm.imei.trim()) {
      alert('Please enter a valid IMEI.');
      return;
    }
    try {
      const res = await correctImei({
        id: historyData.imeiRecord.id,
        imei: correctForm.imei.trim(),
        secondaryImei: correctForm.secondaryImei.trim() || undefined,
        reason: correctForm.reason.trim() || undefined,
      }).unwrap();

      setNotification({
        open: true,
        message: res.message || 'IMEI digits updated successfully.',
        severity: 'success',
      });
      setCorrectModalOpen(false);
      setSearchTerm(correctForm.imei.trim());
      setQueryTerm(correctForm.imei.trim());
    } catch (err) {
      alert(err.data?.message || 'Failed to update IMEI.');
    }
  };

  const currentRecord = historyData?.imeiRecord;
  const currentSupplier =
    currentRecord?.supplier?.name ||
    currentRecord?.purchaseOrderItem?.purchaseOrder?.supplier?.name ||
    '-';

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            IMEI Lifecycle & Supplier Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track serial lifecycles, perform direct supplier exchanges (1-to-1 replacements without purchase/return invoices), and manage supplier links.
          </Typography>
        </Box>
      </Box>

      {notification.open && (
        <Alert
          severity={notification.severity}
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          sx={{ mb: 2 }}
        >
          {notification.message}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} sx={{ mb: 3 }}>
        <Tab label="IMEI Lookup & Actions" icon={<SearchIcon />} iconPosition="start" />
        <Tab label="Inventory IMEI Directory" icon={<QrCodeScannerIcon />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <form onSubmit={handleSearch}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 9 }}>
                  <TextField
                    fullWidth
                    label="Scan or Enter IMEI / Serial Number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="e.g. 861234567890123"
                    autoFocus
                    slotProps={{
                      input: {
                        startAdornment: <QrCodeScannerIcon color="action" sx={{ mr: 1 }} />,
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Button
                    variant="contained"
                    type="submit"
                    size="large"
                    fullWidth
                    sx={{ height: 56, fontWeight: 600 }}
                    disabled={!searchTerm.trim()}
                    startIcon={<SearchIcon />}
                  >
                    Search History
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              IMEI not found in inventory. Please verify the serial number.
            </Alert>
          )}

          {currentRecord && (
            <>
              {/* Linked Exchange Banners */}
              {currentRecord.replacedBy && (
                <Alert severity="info" icon={<ArrowForwardIcon />} sx={{ mb: 2 }}>
                  <strong>Replaced by Supplier:</strong> This unit was exchanged with the supplier for replacement unit with IMEI{' '}
                  <Button
                    size="small"
                    variant="text"
                    sx={{ fontWeight: 700, textTransform: 'none' }}
                    onClick={() => handleInspectImei(currentRecord.replacedBy.imei)}
                  >
                    {currentRecord.replacedBy.imei}
                  </Button>
                </Alert>
              )}
              {currentRecord.replacedFrom && (
                <Alert severity="success" icon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                  <strong>Supplier Replacement:</strong> This unit is a direct replacement from supplier for previous IMEI{' '}
                  <Button
                    size="small"
                    variant="text"
                    sx={{ fontWeight: 700, textTransform: 'none' }}
                    onClick={() => handleInspectImei(currentRecord.replacedFrom.imei)}
                  >
                    {currentRecord.replacedFrom.imei}
                  </Button>
                </Alert>
              )}

              {/* Current Status Card */}
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Primary Identifier
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {currentRecord.imei}
                    </Typography>
                    {currentRecord.secondaryImei && (
                      <Typography variant="body2" color="text.secondary">
                        Secondary IMEI: {currentRecord.secondaryImei}
                      </Typography>
                    )}
                  </Box>

                  {/* Actions Toolbar */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<SwapHorizIcon />}
                      onClick={handleOpenExchange}
                      disabled={currentRecord.status === 'sold' || currentRecord.status === 'supplier_replaced'}
                      sx={{ fontWeight: 600 }}
                    >
                      Supplier Exchange (New IMEI)
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<BusinessIcon />}
                      onClick={handleOpenReassign}
                      sx={{ fontWeight: 600 }}
                    >
                      Change Supplier
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<EditIcon />}
                      onClick={handleOpenCorrect}
                      sx={{ fontWeight: 600 }}
                    >
                      Correct Typo
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Product</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {currentRecord.product?.name || 'N/A'}
                    </Typography>
                    {currentRecord.product?.sku && (
                      <Typography variant="caption" color="text.secondary">
                        SKU: {currentRecord.product.sku}
                      </Typography>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={currentRecord.status.replace(/_/g, ' ').toUpperCase()}
                        color={getStatusColor(currentRecord.status)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Assigned Supplier</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {currentSupplier}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Stock Location</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {currentRecord.location?.name || 'Main Warehouse'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Lifecycle History Timeline */}
              {historyData.history && historyData.history.length > 0 && (
                <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 2 }}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Complete Lifecycle & Exchange Audit Trail
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Event / Action</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Details & Notes</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>User / Operator</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyData.history.map((record) => (
                          <TableRow key={record.id} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {new Date(record.createdAt).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={(record.title || record.action).toUpperCase()}
                                color={getActionColor(record.action)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              {record.referenceType === 'PurchaseOrder' && (
                                <Box>
                                  <Typography variant="body2" color="primary.main" fontWeight={600}>
                                    PO: {record.purchaseOrder?.orderNumber || record.purchaseOrder?.invoiceNumber || '-'}
                                  </Typography>
                                  {record.supplier && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Supplier: {record.supplier.name}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {record.referenceType === 'SalesOrder' && (
                                <Box>
                                  <Typography variant="body2" color="info.main" fontWeight={600}>
                                    INV: {record.salesOrder?.invoiceNumber || record.salesOrder?.orderNumber || '-'}
                                  </Typography>
                                  {record.customer && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Customer: {record.customer.name}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {record.referenceType === 'ImeiLog' && (
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Direct Audit
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{record.description || record.notes || '-'}</Typography>
                              {record.logData?.supplierRmaRef && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  RMA Ref: {record.logData.supplierRmaRef}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{record.user || 'System'}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {/* Directory Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Search IMEI / Product"
              value={dirSearch}
              onChange={(e) => {
                setDirSearch(e.target.value);
                setDirPage(1);
              }}
              sx={{ minWidth: 260 }}
            />
            <TextField
              size="small"
              select
              label="Status Filter"
              value={dirStatus}
              onChange={(e) => {
                setDirStatus(e.target.value);
                setDirPage(1);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="in_stock">In Stock</MenuItem>
              <MenuItem value="sold">Sold</MenuItem>
              <MenuItem value="supplier_replaced">Supplier Replaced</MenuItem>
              <MenuItem value="returned">Returned</MenuItem>
              <MenuItem value="rma">RMA</MenuItem>
            </TextField>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>IMEI / Serial</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dirLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : directoryData?.imeis?.length > 0 ? (
                  directoryData.imeis.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {item.imei}
                        {item.secondaryImei && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Sec: {item.secondaryImei}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.product?.name || '-'}</TableCell>
                      <TableCell>
                        {item.supplier?.name ||
                          item.purchaseOrderItem?.purchaseOrder?.supplier?.name ||
                          '-'}
                      </TableCell>
                      <TableCell>{item.location?.name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.status.replace(/_/g, ' ').toUpperCase()}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleInspectImei(item.imei)}
                        >
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No IMEIs found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* MODAL 1: Supplier Device Exchange */}
      <Dialog open={exchangeModalOpen} onClose={() => setExchangeModalOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleExchangeSubmit}>
          <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon color="secondary" />
            1-to-1 Supplier Device Exchange
          </DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              This retires current IMEI <strong>{currentRecord?.imei}</strong> as <em>'supplier_replaced'</em> and brings the replacement IMEI into stock at zero cost with no purchase or return accounting entry.
            </Alert>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  label="New Replacement IMEI"
                  placeholder="Scan or type new IMEI"
                  value={exchangeForm.newImei}
                  onChange={(e) => setExchangeForm((prev) => ({ ...prev, newImei: e.target.value }))}
                  autoFocus
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="New Secondary IMEI (Optional)"
                  placeholder="e.g. Dual SIM IMEI"
                  value={exchangeForm.newSecondaryImei}
                  onChange={(e) => setExchangeForm((prev) => ({ ...prev, newSecondaryImei: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  select
                  label="Supplier"
                  value={exchangeForm.supplierId}
                  onChange={(e) => setExchangeForm((prev) => ({ ...prev, supplierId: e.target.value }))}
                >
                  <MenuItem value="">-- Select Supplier --</MenuItem>
                  {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} {s.phone ? `(${s.phone})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Supplier RMA / Exchange Reference #"
                  placeholder="e.g. RMA-89412 or Supplier Slip No"
                  value={exchangeForm.supplierRmaRef}
                  onChange={(e) => setExchangeForm((prev) => ({ ...prev, supplierRmaRef: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Exchange Reason / Defect Notes"
                  placeholder="e.g. Screen flickering under warranty replaced by vendor"
                  value={exchangeForm.reason}
                  onChange={(e) => setExchangeForm((prev) => ({ ...prev, reason: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setExchangeModalOpen(false)} disabled={isExchanging}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isExchanging || !exchangeForm.newImei.trim()}
              startIcon={isExchanging ? <CircularProgress size={18} /> : <CheckCircleIcon />}
            >
              {isExchanging ? 'Exchanging...' : 'Execute Supplier Exchange'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* MODAL 2: Change Supplier */}
      <Dialog open={reassignModalOpen} onClose={() => setReassignModalOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleReassignSubmit}>
          <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            Reassign Supplier
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update the supplier association for IMEI <strong>{currentRecord?.imei}</strong> directly.
            </Typography>
            <TextField
              fullWidth
              select
              required
              label="Select Supplier"
              value={reassignForm.supplierId}
              onChange={(e) => setReassignForm((prev) => ({ ...prev, supplierId: e.target.value }))}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">-- Select Supplier --</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason for Reassignment"
              value={reassignForm.reason}
              onChange={(e) => setReassignForm((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setReassignModalOpen(false)} disabled={isReassigning}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isReassigning || !reassignForm.supplierId}
            >
              {isReassigning ? 'Saving...' : 'Save Supplier'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* MODAL 3: Correct Typo */}
      <Dialog open={correctModalOpen} onClose={() => setCorrectModalOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleCorrectSubmit}>
          <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Correct IMEI Digits
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fix misread or typo digits on this IMEI record directly.
            </Typography>
            <TextField
              fullWidth
              required
              label="Primary IMEI"
              value={correctForm.imei}
              onChange={(e) => setCorrectForm((prev) => ({ ...prev, imei: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Secondary IMEI (Optional)"
              value={correctForm.secondaryImei}
              onChange={(e) => setCorrectForm((prev) => ({ ...prev, secondaryImei: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason / Note"
              value={correctForm.reason}
              onChange={(e) => setCorrectForm((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCorrectModalOpen(false)} disabled={isCorrecting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCorrecting || !correctForm.imei.trim()}
            >
              {isCorrecting ? 'Updating...' : 'Update IMEI'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default ImeiTracking;
